import { useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DOWNLOAD_BANNER_STATUS,
  DownloadBannerPayload,
  DownloadBannerType,
} from 'models/Download';
import useSocketEvent from 'services/socketService/hooks/useSocketEvent';
import {
  DownloadedSuccessfullyEventData,
  DownloadFailedEventData,
} from 'services/socketService/socketEventData';
import downloadService from 'services/downloadService';
import { isPdf } from 'utils';
import GeneralContext from 'providers/General/General.context';
import { EXPORT_KIND_CONFIG } from './data';

type DownloadSuccessfully = (
  payload: DownloadedSuccessfullyEventData,
  downloadType?: DownloadBannerType,
  shouldOpenInNewWindow?: boolean,
) => void;

type DownloadFailed = (
  payload: DownloadFailedEventData,
  downloadType?: DownloadBannerType,
) => void;

type UseExportDownloadStatusOptions = {
  showDownloadBanner: (payload: DownloadBannerPayload) => void;
  downloadSuccessfully: DownloadSuccessfully;
  downloadFailed: DownloadFailed;
};

export default function useExportDownloadStatus(
  kind: DownloadBannerType,
  options: UseExportDownloadStatusOptions,
) {
  const { showDownloadBanner, downloadSuccessfully, downloadFailed } = options;

  const { isSafari } = useContext(GeneralContext);

  const { t } = useTranslation();

  const {
    type,
    completedEvent,
    failedEvent,
    secondaryCompletedEvent,
    secondaryFailedEvent,
    inProgressTitleKey,
    inProgressMessageKey,
    failTitleKey,
    usesPdfWindow,
    shouldOpenInNewWindow,
  } = EXPORT_KIND_CONFIG[kind] || {};

  const [isInProgress, setIsInProgress] = useState(false);

  const pdfWindowRef = useRef<Window | null>(null);

  const start = useCallback(
    (message?: string, openInNewTab = false) => {
      setIsInProgress(true);

      if (usesPdfWindow && isSafari && openInNewTab)
        pdfWindowRef.current = window.open('', '_blank');

      showDownloadBanner({
        status: DOWNLOAD_BANNER_STATUS.PREPARING,
        type,
        message,
      });
    },
    [usesPdfWindow, isSafari, showDownloadBanner, type],
  );

  const closePdfWindow = useCallback(() => {
    if (usesPdfWindow && pdfWindowRef.current) {
      pdfWindowRef.current.close();
      pdfWindowRef.current = null;
    }
  }, [usesPdfWindow]);

  const fail = useCallback(() => {
    setIsInProgress(false);

    closePdfWindow();

    showDownloadBanner({
      status: DOWNLOAD_BANNER_STATUS.ERROR,
      type,
      ...(failTitleKey && { customTitle: t(failTitleKey) }),
    });
  }, [closePdfWindow, showDownloadBanner, type, failTitleKey, t]);

  const shouldIgnoreThisPayloadId = useCallback(
    (uniqueId: string) => uniqueId !== downloadService.downloadUniqueId,
    [],
  );

  const onCompleted = useCallback(
    (payload: DownloadedSuccessfullyEventData) => {
      if (shouldIgnoreThisPayloadId(payload.uniqueId)) return;

      setIsInProgress(false);

      if (usesPdfWindow && pdfWindowRef.current) {
        pdfWindowRef.current.location.href = payload.url;
        pdfWindowRef.current = null;

        showDownloadBanner({
          status: DOWNLOAD_BANNER_STATUS[payload.url ? 'SUCCESS' : 'ERROR'],
          type,
        });

        return;
      }

      downloadSuccessfully(
        payload,
        type,
        shouldOpenInNewWindow && (!payload?.type || isPdf(payload?.type)),
      );
    },
    [
      shouldIgnoreThisPayloadId,
      usesPdfWindow,
      downloadSuccessfully,
      type,
      shouldOpenInNewWindow,
      showDownloadBanner,
    ],
  );

  const onFailed = useCallback(
    (payload: DownloadFailedEventData) => {
      if (shouldIgnoreThisPayloadId(payload.uniqueId)) return;

      setIsInProgress(false);

      closePdfWindow();

      downloadFailed(payload, type);
    },
    [closePdfWindow, downloadFailed, shouldIgnoreThisPayloadId, type],
  );

  const showInProgressBanner = useCallback(
    () =>
      showDownloadBanner({
        status: DOWNLOAD_BANNER_STATUS.PREPARING,
        type,
        ...(inProgressTitleKey && { customTitle: t(inProgressTitleKey) }),
        ...(inProgressMessageKey && { message: t(inProgressMessageKey) }),
        isClosable: true,
      }),
    [showDownloadBanner, type, inProgressTitleKey, inProgressMessageKey, t],
  );

  useSocketEvent(completedEvent, onCompleted);
  useSocketEvent(failedEvent, onFailed);

  useSocketEvent(secondaryCompletedEvent, onCompleted);
  useSocketEvent(secondaryFailedEvent, onFailed);

  return { isInProgress, start, fail, showInProgressBanner };
}
